import React, { useCallback, useEffect, useState } from "react";
import { BiPlus } from "react-icons/bi";
import { useOutletContext } from "react-router";
import { useProject } from "../../../utils/hooks/useProject";
import API from "../../../utils/API";
import Loading from "../../../utils/components/Loading";
import ReportRowItem from "./ReportRowItem";
import toastr from "toastr";

export default function ProjectReports () {
  const { project } = useProject();
  const [translate] = useOutletContext();

  const [state, setState] = useState({
    isNew: false,
    roles: [],
    reports: [],
    loading: {
      roles: false,
      reports: false,
      submit: false,
      delete: false,
    },
    newReport: {
      name: "",
      roles: [],
      link: "",
    },
  });

  const { isNew, roles, reports, loading, newReport } = state;

  const updateState = (updates) => {
    setState(prev => ({ ...prev, ...updates }));
  };

  const setLoading = (key, value) => {
    setState(prev => ({
      ...prev,
      loading: { ...prev.loading, [key]: value },
    }));
  };

  const resetNewReport = () => {
    updateState({
      isNew: false,
      newReport: { name: "", roles: [], link: "" },
    });
  };

  const getReports = useCallback(() => {
    if (!project?.id) return;

    setLoading("reports", true);
    API.getData(`/project_report/${project.id}?page=1&size=99`)
      .then(({ data: res }) => {
        updateState({ reports: res?.data || [] });
      })
      .catch(() => {})
      .finally(() => {
        setLoading("reports", false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, translate]);

  const getRoles = useCallback(() => {
    if (!project?.id) return;

    setLoading("roles", true);
    API.getData(`/roles?project_id=${project.id}`)
      .then(({ data: res }) => {
        const formattedRoles = res.data.map((item) =>
          ({
            value: item.id,
            label: item.name,
          }));

        updateState({ roles: formattedRoles });
      })
      .catch(() => {})
      .finally(() => {
        setLoading("roles", false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, translate]);

  const handleNewReportChange = (field, value) => {
    setState(prev => ({
      ...prev,
      newReport: { ...prev.newReport, [field]: value },
    }));
  };

  const handleSave = () => {
    if (!project?.id) return;

    const filledAllFields =
      newReport.name &&
      newReport.link &&
      newReport.roles.length > 0;

    if (!filledAllFields) {
      toastr.error(translate("project_settings.reports.fill_all_fields"));
      return;
    }

    setLoading("submit", true);
    API.postData(`/project_report/${project.id}`, {
      link: newReport.link,
      name: newReport.name,
      roles: newReport.roles.map((role) => role.value),
    })
      .then(() => {
        toastr.success(translate("project_settings.reports.report_created_successfully"));
        resetNewReport();
        getReports();
      })
      .catch(() => {})
      .finally(() => {
        setLoading("submit", false);
      });
  };

  const handleDelete = (reportId) => {
    setLoading("delete", true);
    API.deleteData(`/project_report/${reportId}`)
      .then(() => {
        toastr.success(translate("project_settings.reports.report_deleted_successfully"));
        getReports();
      })
      .catch(() => {})
      .finally(() => {
        setLoading("delete", false);
      });
  };

  const toggleNewReport = () => {
    if (!project?.id) {
      toastr.warning(translate("select_a_project_alert"));
      return;
    }
    updateState({ isNew: !isNew });
  };

  useEffect(() => {
    getRoles();
    getReports();
  }, [getRoles, getReports]);

  const renderAddButton = () => (
    <div
      className="d-flex align-items-center action-button mb-4"
      role="button"
      onClick={toggleNewReport}
    >
      <BiPlus className="fs-1 bg-primary text-white rounded-circle"/>
      <span className="ms-2 fw-medium">
        {translate("project_settings.reports.create_report_button")}
      </span>
    </div>
  );

  const renderReportsList = () => {
    if (loading.reports) return <Loading/>;

    return (
      <div className="d-flex flex-column gap-3">
        {isNew && (
          <ReportRowItem
            translate={translate}
            isNew={true}
            roles={roles}
            report={newReport}
            onReportChange={handleNewReportChange}
            onSave={handleSave}
            onClose={resetNewReport}
            isLoading={{
              roles: loading.roles,
              submit: loading.submit,
            }}
          />
        )}

        {reports.length > 0 ? (
          reports.map((report) => (
            <ReportRowItem
              key={report.id}
              translate={translate}
              isNew={false}
              roles={roles}
              report={report}
              onDelete={() => handleDelete(report.id)}
              isLoading={{
                delete: loading.delete,
              }}
            />
          ))
        ) : (
          !isNew && (
            <div className="text-center py-4 text-muted">
              {translate("project_settings.reports.no_reports")}
            </div>
          )
        )}
      </div>
    );
  };

  return (
    <div className="ProjectReports">
      <div className="row">
        <div className="col-12 d-flex">
          {renderAddButton()}
        </div>
      </div>

      <div className="row py-3 mt-3">
        <div className="col-12 col-lg-11">
          {renderReportsList()}
        </div>
      </div>
    </div>
  );
}